/**
 * Centralized error handling utilities
 * Provides type-safe error handling for API calls and other error scenarios
 */

import { message as antMessage } from 'antd';
import { logger, sanitizeError } from './logger';

/**
 * Error severity levels for categorizing errors
 */
export enum ErrorSeverity {
    /** Critical errors that should be shown to the user and logged to error tracking */
    CRITICAL = 'critical',
    /** Warning errors that should be logged but not necessarily shown to user */
    WARNING = 'warning',
    /** Informational errors that are logged for debugging */
    INFO = 'info',
}

/**
 * Context information to include with error reports
 */
export interface ErrorContext {
    /** Optional conversation ID */
    conversationId?: string;
    /** Optional message ID */
    messageId?: string;
    /** Operation being performed when error occurred */
    operation: string;
    /** Optional patient ID */
    patientId?: string;
    /** Optional correlation ID */
    correlationId?: string;
    /** Additional context data */
    [key: string]: unknown;
}

/**
 * Declare Sentry type for window object
 */
declare global {
    interface Window {
        Sentry?: {
            captureException: (
                error: unknown,
                options?: { level?: string; contexts?: Record<string, unknown> }
            ) => void;
        };
    }
}

/**
 * API Error structure from RTK Query
 */
export interface ApiError {
    status?: number | string;
    data?: {
        message?: string;
        error?: string;
        detail?: string; // FastAPI error format
        statusCode?: number;
    };
}

/**
 * Type guard to check if an error is an API error from RTK Query
 */
export function isApiError(error: unknown): error is ApiError {
    return typeof error === 'object' && error !== null && 'status' in error && ('data' in error || 'error' in error);
}

/**
 * Type guard to check if error is a standard Error object
 */
export function isError(error: unknown): error is Error {
    return error instanceof Error;
}

/**
 * Extract a user-friendly error message from any error type
 */
export function getErrorMessage(error: unknown, defaultMessage = 'An unexpected error occurred'): string {
    // Handle API errors from RTK Query
    if (isApiError(error)) {
        // Try to get message from various possible locations
        if (error.data?.message) {
            return error.data.message;
        }
        if (error.data?.error) {
            return error.data.error;
        }
        // FastAPI error format
        if (error.data?.detail) {
            return error.data.detail;
        }
        // Handle status-based messages
        if (error.status === 401) {
            return 'Authentication required. Please log in again.';
        }
        if (error.status === 403) {
            return 'You do not have permission to perform this action.';
        }
        if (error.status === 404) {
            return 'The requested resource was not found.';
        }
        if (error.status === 500) {
            return 'Server error. Please try again later.';
        }
        if (typeof error.status === 'number' && error.status >= 400) {
            return `Request failed with status ${error.status}`;
        }
    }

    // Handle standard Error objects
    if (isError(error)) {
        return error.message;
    }

    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    // Fallback to default message
    return defaultMessage;
}

/**
 * Sanitize sensitive data from error objects before logging
 * Removes tokens, passwords, API keys, and other sensitive fields
 * Re-exported from logger.ts to avoid circular dependency
 */
export { sanitizeError } from './logger';

/**
 * Log error with consistent formatting
 * In production, this would send to error tracking service
 * Automatically sanitizes sensitive data before logging
 */
export function logError(error: unknown, context?: string): void {
    const sanitized = sanitizeError(error);
    if (context) {
        logger.error(`[${context}]`, sanitized);
    } else {
        logger.error('Error', sanitized);
    }

    // TODO: Send to error tracking service in production
    // Example:
    // if (!isDevelopment) {
    //     const sanitized = sanitizeError(error);
    //     Sentry.captureException(sanitized, {
    //         tags: { context },
    //     });
    // }
}

/**
 * Handle error with logging and user message extraction
 * Returns a user-friendly error message
 */
export function handleError(error: unknown, context?: string, defaultMessage = 'An unexpected error occurred'): string {
    logError(error, context);
    return getErrorMessage(error, defaultMessage);
}

/**
 * Async wrapper that provides consistent error handling
 * Usage:
 * ```
 * const [data, error] = await withErrorHandling(
 *   () => myAsyncFunction(),
 *   'MyComponent.fetchData'
 * );
 * ```
 */
export async function withErrorHandling<T>(fn: () => Promise<T>, context?: string): Promise<[T | null, string | null]> {
    try {
        const result = await fn();
        return [result, null];
    } catch (error: unknown) {
        const errorMessage = handleError(error, context);
        return [null, errorMessage];
    }
}

/**
 * Network error detector
 */
export function isNetworkError(error: unknown): boolean {
    if (isApiError(error)) {
        return error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR';
    }
    if (isError(error)) {
        return (
            error.message.includes('network') || error.message.includes('fetch') || error.message.includes('timeout')
        );
    }
    return false;
}

/**
 * Timeout error detector
 */
export function isTimeoutError(error: unknown): boolean {
    if (isApiError(error)) {
        return error.status === 'TIMEOUT_ERROR';
    }
    if (isError(error)) {
        return error.message.toLowerCase().includes('timeout');
    }
    return false;
}

/**
 * Validation error detector
 */
export function isValidationError(error: unknown): boolean {
    if (isApiError(error)) {
        return error.status === 400 || error.status === 422;
    }
    return false;
}

/**
 * Handle chat-specific errors with severity levels, logging, and user notifications
 * This function provides comprehensive error handling for chat operations including:
 * - Logging with context
 * - Error tracking service integration (Sentry)
 * - User notifications for critical errors
 *
 * @param error - The error to handle
 * @param context - Context information about where/when the error occurred
 * @param severity - How severe the error is (determines logging and user notification behavior)
 *
 * @example
 * ```typescript
 * try {
 *   await sendMessage(...);
 * } catch (error) {
 *   handleChatError(error, {
 *     operation: 'sendMessage',
 *     conversationId,
 *   }, ErrorSeverity.CRITICAL);
 * }
 * ```
 */
export function handleChatError(
    error: unknown,
    context: ErrorContext,
    severity: ErrorSeverity = ErrorSeverity.WARNING
): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    // Sanitize error for logging
    const sanitizedError = sanitizeError(error);

    // Prepare structured error log
    const errorLog = {
        severity: severity.toUpperCase(),
        operation: context.operation,
        message: errorMessage,
        stack: errorStack,
        context: {
            conversationId: context.conversationId,
            messageId: context.messageId,
            patientId: context.patientId,
            correlationId: context.correlationId,
            ...Object.fromEntries(
                Object.entries(context).filter(
                    ([key]) => !['operation', 'conversationId', 'messageId', 'patientId', 'correlationId'].includes(key)
                )
            ),
        },
        timestamp: new Date().toISOString(),
        sanitizedError,
    };

    // Always log using logger
    if (severity === ErrorSeverity.CRITICAL) {
        logger.error(`[CRITICAL ERROR] ${context.operation}`, errorLog);
    } else if (severity === ErrorSeverity.WARNING) {
        logger.warn(`[WARNING] ${context.operation}`, errorLog);
    } else {
        logger.error(`[INFO] ${context.operation}`, errorLog);
    }

    // Report to error tracking service (Sentry)
    if (typeof window !== 'undefined' && window.Sentry) {
        try {
            window.Sentry.captureException(error, {
                level: severity,
                contexts: {
                    chat: context,
                    errorDetails: errorLog,
                },
            });
        } catch (sentryError) {
            // Don't let Sentry errors break the application
            logger.error('Failed to report to Sentry', sentryError);
        }
    }

    // Show user notification for critical errors
    if (severity === ErrorSeverity.CRITICAL) {
        const userMessage = getErrorMessage(
            error,
            `Error in ${context.operation}. Please try again or contact support.`
        );

        antMessage.error({
            content: userMessage,
            duration: 5,
            key: `error-${context.operation}-${Date.now()}`,
        });
    }
}

/**
 * Handle cache update errors specifically
 * These are typically non-critical but should be logged for debugging
 */
export function handleCacheUpdateError(error: unknown, cacheKey: string, conversationId: string): void {
    handleChatError(
        error,
        {
            operation: 'updateChatCache',
            conversationId,
            cacheKey,
        },
        ErrorSeverity.WARNING
    );
}

/**
 * Handle session storage errors
 * These are typically non-critical as they're used for state recovery
 */
export function handleSessionStorageError(error: unknown, operation: string, conversationId?: string): void {
    handleChatError(
        error,
        {
            operation: `sessionStorage.${operation}`,
            conversationId,
        },
        ErrorSeverity.INFO
    );
}

/**
 * Handle custom event dispatch errors
 * These are non-critical as they're used for cross-component communication
 */
export function handleEventDispatchError(error: unknown, eventName: string, conversationId?: string): void {
    handleChatError(
        error,
        {
            operation: `dispatchEvent.${eventName}`,
            conversationId,
        },
        ErrorSeverity.INFO
    );
}
